import { ArrowRight, Flame, Layers, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { AdSlot } from "../../components/AdSlot";
import { ArticleCard } from "../../components/ArticleCard";
import { EmptyState, ErrorState, LoadingState } from "../../components/LoadingState";
import { MagazineHero } from "../../components/MagazineHero";
import { NewsletterBox } from "../../components/NewsletterBox";
import { Seo } from "../../components/Seo";
import { SponsoredSection } from "../../components/SponsoredSection";
import { useApi } from "../../hooks/useApi";
import type { Advertisement, HomepageSection } from "../../types";

type HomePayload = {
  sections: HomepageSection[];
  advertisements: Advertisement[];
};

export function HomePage() {
  const { data, loading, error } = useApi<HomePayload>("/api/homepage", []);

  if (loading) return <LoadingState label="Preparing the current edition of ZimSmart..." />;
  if (error) return <ErrorState message={error} />;
  if (!data?.sections.length) {
    return (
      <div className="mag-container">
        <EmptyState
          title="No published stories yet"
          body="Use ZimSmart Newsroom to publish articles and curate the front page."
        />
      </div>
    );
  }

  // Extract sections
  const heroSection = data.sections.find(
    (s) =>
      s.section_type === "hero" ||
      s.section_key === "hero-stories" ||
      s.section_key === "lead-stories"
  );
  const trendingSection = data.sections.find(
    (s) => s.section_type === "trending" || s.section_key === "trending-now"
  );
  const otherSections = data.sections.filter(
    (s) => s.id !== heroSection?.id && s.id !== trendingSection?.id
  );

  const heroArticles = heroSection?.articles || [];
  const primaryCoverStory = heroArticles[0];
  const leadSupportStories = heroArticles.slice(1, 5);

  const trendingArticles = trendingSection?.articles || [];

  return (
    <>
      <Seo
        title="ZimSmart — Zimbabwe's Premier Journal of Technology, Business & Innovation"
        description="Independent journalism covering Zimbabwe's digital economy, fintech, renewable power, startups, and modern living."
        canonical="https://zimsmart.co.zw/"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://zimsmart.co.zw/#organization", name: "ZimSmart", url: "https://zimsmart.co.zw/" },
            { "@type": "WebSite", "@id": "https://zimsmart.co.zw/#website", name: "ZimSmart", url: "https://zimsmart.co.zw/", publisher: { "@id": "https://zimsmart.co.zw/#organization" } }
          ]
        }}
      />

      <div className="mag-container mag-home-flow">
        {/* Top Sponsored Billboard Ad */}
        <AdSlot ads={data.advertisements} placement="homepage_top" className="mb-4" />

        {/* SECTION 1: RESPONSIVE MAGAZINE HERO COMPONENT */}
        {primaryCoverStory ? (
          <MagazineHero
            primaryArticle={primaryCoverStory}
            sidebarArticles={leadSupportStories}
            editionBadge="VOL. IV • DAILY EDITION"
          />
        ) : (
          <EmptyState
            title="Lead story slot open"
            body="Select a cover article in Admin -> Homepage."
          />
        )}

        {/* SECTION 2: TRENDING & INTELLIGENCE PULSE */}
        {trendingArticles.length > 0 && (
          <section className="mag-trending-strip-section" aria-label="Trending Insights">
            <div className="mag-trending-strip-header">
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className="text-amber-600" />
                <h2 className="text-sm font-extrabold uppercase tracking-wider text-slate-900">
                  TRENDING DISPATCHES
                </h2>
              </div>
              <Link to="/technology" className="mag-view-all-link">
                <span>View All Tech</span>
                <ArrowRight size={13} />
              </Link>
            </div>
            <div className="mag-trending-grid">
              {trendingArticles.map((article, idx) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  variant="compact"
                  rankIndex={idx + 1}
                />
              ))}
            </div>
          </section>
        )}

        {/* Mid-page Ad / Sponsorship Banner */}
        <AdSlot ads={data.advertisements} placement="homepage_inline" className="my-6" />

        {/* SECTION 3: SPONSORED INTELLIGENCE & PARTNER BRIEFS */}
        <SponsoredSection ads={data.advertisements} />

        {/* SECTION 4: EDITORIAL CATEGORIES & DEEP DIVES */}
        {otherSections.map((section) => {
          if (section.section_type === "newsletter") {
            return <NewsletterBox key={section.id} />;
          }

          const isCategory = section.section_type === "category";
          const articles = section.articles || [];
          const firstArticle = articles[0];
          const restArticles = articles.slice(1);

          return (
            <section
              key={section.id}
              className="mag-editorial-section"
              aria-labelledby={`sec-title-${section.id}`}
            >
              <div className="mag-section-header">
                <div className="mag-section-title-wrap">
                  <span className="mag-section-indicator" />
                  <h2 id={`sec-title-${section.id}`} className="mag-section-title">
                    {section.title}
                  </h2>
                </div>
                {section.category_slug && (
                  <Link to={`/${section.category_slug}`} className="mag-view-all-link">
                    <span>Explore Section</span>
                    <ArrowRight size={13} />
                  </Link>
                )}
              </div>

              {articles.length ? (
                isCategory && articles.length >= 3 ? (
                  /* High-impact Magazine Split: 1 Featured story on left, stack on right */
                  <div className="mag-category-split">
                    <div className="mag-category-featured">
                      {firstArticle && <ArticleCard article={firstArticle} variant="featured" />}
                    </div>
                    <div className="mag-category-stack">
                      {restArticles.map((art) => (
                        <ArticleCard key={art.id} article={art} variant="horizontal" />
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Clean Editorial 3-Column Grid */
                  <div className="mag-articles-grid">
                    {articles.map((art) => (
                      <ArticleCard key={art.id} article={art} variant="compact" />
                    ))}
                  </div>
                )
              ) : (
                <EmptyState
                  title="Stories coming soon"
                  body="Articles assigned to this section will populate automatically upon publication."
                />
              )}
            </section>
          );
        })}

        {/* Bottom Newsletter Dispatch */}
        <NewsletterBox />
      </div>
    </>
  );
}
